export type ConferenceAccess =
  | { readonly mode: "all" }
  | { readonly mode: "selected"; readonly conferenceIds: readonly string[] };
export interface ManagedUserAccess {
  readonly authUid: string;
  readonly email: string;
  readonly role: "admin" | "viewer";
  readonly status: "pending" | "active" | "disabled";
  readonly mfaEnrolled: boolean;
  readonly conferenceAccess: ConferenceAccess;
  readonly revision: number;
  readonly canEdit: boolean;
  readonly canDelete?: boolean;
  readonly deletionPending?: boolean;
}
export interface UserAccessDirectory {
  readonly users: readonly ManagedUserAccess[];
  readonly conferences: readonly {
    conferenceId: string;
    name: string;
    displayNames: { ar: string; en: string } | null;
  }[];
}
export interface AccessChange {
  readonly role: "admin" | "viewer";
  readonly conferenceAccess: ConferenceAccess;
  readonly expectedRevision: number;
}
export function validConferenceAccess(
  value: unknown,
): value is ConferenceAccess {
  if (!value || typeof value !== "object" || !("mode" in value)) return false;
  return (
    value.mode === "all" ||
    (value.mode === "selected" &&
      "conferenceIds" in value &&
      Array.isArray(value.conferenceIds) &&
      value.conferenceIds.every((id) => typeof id === "string"))
  );
}
export function parseAccessDirectory(value: unknown): UserAccessDirectory {
  if (
    !value ||
    typeof value !== "object" ||
    !("users" in value) ||
    !Array.isArray(value.users) ||
    !("conferences" in value) ||
    !Array.isArray(value.conferences)
  )
    throw new Error("invalid_access_response");
  if (
    !value.users.every(
      (user) =>
        user &&
        typeof user.authUid === "string" &&
        typeof user.email === "string" &&
        ["admin", "viewer"].includes(user.role) &&
        ["active", "pending", "disabled"].includes(user.status) &&
        typeof user.mfaEnrolled === "boolean" &&
        Number.isSafeInteger(user.revision) &&
        typeof user.canEdit === "boolean" &&
        (user.canDelete === undefined || typeof user.canDelete === "boolean") &&
        (user.deletionPending === undefined ||
          typeof user.deletionPending === "boolean") &&
        validConferenceAccess(user.conferenceAccess),
    )
  )
    throw new Error("invalid_access_response");
  if (
    !value.conferences.every(
      (item) =>
        item &&
        typeof item.conferenceId === "string" &&
        typeof item.name === "string" &&
        (item.displayNames === null ||
          (typeof item.displayNames?.ar === "string" &&
            typeof item.displayNames?.en === "string")),
    )
  )
    throw new Error("invalid_access_response");
  return value as UserAccessDirectory;
}
